// ===========================================
// Génération du bordereau de paiement (PDF) — partagé
// Utilisé après l'enregistrement d'un règlement (auto)
// et depuis le détail règlement (bouton « Imprimer »).
// ===========================================
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import logoCap from '../../assets/logo/logo-cap-assurances.JPG';
import { formatPhone as formatPhoneShared, formatMontantPdf } from './format';

const MODE_LABELS = {
    especes: 'Espèces',
    cheque: 'Chèque',
    virement: 'Virement',
    mobile_money: 'Mobile Money',
};

const formatPhone = (tel) => formatPhoneShared(tel, { fallback: '' });
// Espaces fines/insécables (U+202F, U+00A0) non rendues par la police jsPDF :
// formatMontantPdf normalise déjà tous les espaces en espace ASCII standard.
const fmtPdf = (val) => (val == null || val === '' ? '0' : formatMontantPdf(val));

/**
 * Génère et télécharge le bordereau de paiement.
 * @param {object} p
 * @param {object} p.reglement - { montant, moyenPaiement, dateReglement, reference }
 * @param {object} p.contrat   - { numeroPolice, compagnie, typeAssurance, primeTTC, totalVerse, resteAPayer }
 * @param {object} p.client    - { nom, prenom, telephone, code, type, agence }
 * @param {string} p.emetteur  - nom complet du caissier / agent
 */
export function generateBordereauPdf({ reglement, contrat, client, emetteur }) {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const centerX = pageWidth / 2;
    const cName = client ? `${client.nom || ''} ${client.prenom || ''}`.trim() : '';

    try { doc.addImage(logoCap, 'JPEG', 14, 10, 40, 16); } catch (e) { /* logo optionnel */ }

    doc.setFontSize(16); doc.setFont(undefined, 'bold'); doc.setTextColor(26, 43, 95);
    doc.text('BORDEREAU DE PAIEMENT', centerX, 20, { align: 'center' });

    doc.setDrawColor(26, 43, 95); doc.setLineWidth(0.5);
    doc.line(14, 30, pageWidth - 14, 30);

    const policeNum = contrat?.numeroPolice || reglement?.contrat?.numeroPolice || '';
    const colLeft = 14;
    const colRight = centerX + 5;
    let y = 38;

    doc.setFontSize(11); doc.setFont(undefined, 'bold'); doc.setTextColor(26, 43, 95);
    doc.text('INFORMATIONS CLIENT', colLeft, y);
    doc.text('INFORMATIONS CONTRAT', colRight, y);
    y += 2;
    doc.setDrawColor(26, 43, 95); doc.setLineWidth(0.3);
    doc.line(colLeft, y, centerX - 5, y);
    doc.line(colRight, y, pageWidth - 14, y);
    y += 6;

    doc.setFont(undefined, 'normal'); doc.setFontSize(10); doc.setTextColor(0);
    doc.text(`Nom: ${cName}`, colLeft, y);
    doc.text(`N° Police: ${policeNum}`, colRight, y); y += 6;
    doc.text(`Téléphone: ${formatPhone(client?.telephone)}`, colLeft, y);
    doc.text(`Compagnie: ${contrat?.compagnie?.nom || '-'}`, colRight, y); y += 6;
    doc.text(`Code: ${client?.code || ''}`, colLeft, y);
    doc.text(`Type: ${contrat?.typeAssurance?.nom || '-'}`, colRight, y); y += 6;
    doc.text(`Type: ${client?.type === 'entreprise' ? 'Entreprise' : 'Particulier'}`, colLeft, y);
    doc.text(`Prime TTC: ${fmtPdf(contrat?.primeTTC || 0)} FCFA`, colRight, y); y += 6;
    doc.text(`Agence: ${client?.agence?.nom || '-'}`, colLeft, y);
    y += 12;

    const montant = reglement.montant || 0;
    const mode = MODE_LABELS[reglement.moyenPaiement] || reglement.moyenPaiement || '';
    const dateReg = reglement.dateReglement ? new Date(reglement.dateReglement).toLocaleDateString('fr-FR') : '';
    const totalVerse = contrat?.totalVerse != null ? contrat.totalVerse : 0;
    const reste = contrat?.resteAPayer != null ? contrat.resteAPayer : (contrat?.primeTTC || 0) - totalVerse;

    autoTable(doc, {
        startY: y,
        head: [['Designation', 'Montant (FCFA)']],
        body: [
            ['Montant verse ce jour', fmtPdf(montant)],
            ['Mode de paiement', mode],
            ['Date du versement', dateReg],
            ['Référence', reglement.reference || '-'],
            ['', ''],
            ['Prime totale du contrat (TTC)', fmtPdf(contrat?.primeTTC || 0)],
            ['Total deja verse (cumul)', fmtPdf(totalVerse)],
            ['Solde restant a payer', fmtPdf(Math.max(0, reste))],
        ],
        styles: { fontSize: 10, cellPadding: 5, lineColor: [0, 0, 0], lineWidth: 0.3 },
        headStyles: { fillColor: [26, 43, 95], textColor: 255, fontSize: 11, fontStyle: 'bold', halign: 'center' },
        columnStyles: { 0: { fontStyle: 'bold', cellWidth: 100 }, 1: { halign: 'right' } },
        didParseCell: (data) => {
            if (data.section === 'body' && data.row.index === 0 && data.column.index === 1) {
                data.cell.styles.textColor = [82, 196, 26]; data.cell.styles.fontStyle = 'bold'; data.cell.styles.fontSize = 14;
            }
            if (data.section === 'body' && data.row.index === 4) {
                data.cell.styles.minCellHeight = 2; data.cell.styles.cellPadding = 1; data.cell.styles.fillColor = [230, 235, 245];
            }
            if (data.section === 'body' && data.row.index === 5 && data.column.index === 1) {
                data.cell.styles.fontStyle = 'bold'; data.cell.styles.textColor = [26, 43, 95];
            }
            if (data.section === 'body' && data.row.index === 6 && data.column.index === 1) {
                data.cell.styles.fontStyle = 'bold'; data.cell.styles.textColor = [82, 196, 26];
            }
            if (data.section === 'body' && data.row.index === 7) {
                data.cell.styles.fontStyle = 'bold';
                data.cell.styles.fillColor = reste > 0 ? [255, 245, 245] : [240, 255, 240];
                if (data.column.index === 1) {
                    data.cell.styles.textColor = reste > 0 ? [255, 77, 79] : [82, 196, 26];
                    data.cell.styles.fontSize = 13;
                }
            }
        },
        theme: 'grid',
    });

    const pageHeight = doc.internal.pageSize.height;
    const now = new Date();
    const dateEmission = `${now.toLocaleDateString('fr-FR')} a ${now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;

    doc.setDrawColor(26, 43, 95); doc.setLineWidth(0.3);
    doc.line(14, pageHeight - 42, pageWidth - 14, pageHeight - 42);
    doc.setFontSize(9); doc.setFont(undefined, 'normal'); doc.setTextColor(80);
    doc.text(`Date d'emission: ${dateEmission}`, 14, pageHeight - 36);
    doc.text(`Caissier(e): ${emetteur || ''}`, pageWidth - 14, pageHeight - 36, { align: 'right' });

    doc.setDrawColor(0); doc.setLineWidth(0.5);
    doc.line(14, pageHeight - 30, pageWidth - 14, pageHeight - 30);
    doc.setFontSize(8); doc.setFont(undefined, 'bold'); doc.setTextColor(0);
    doc.text('CAP ASSURANCES - Societe de Courtage', centerX, pageHeight - 24, { align: 'center' });
    doc.setFont(undefined, 'normal');
    doc.text('Cocody 2 Plateaux Vallon ; Rue des Jardins, 28 BP 56 Abidjan 28', centerX, pageHeight - 19, { align: 'center' });
    doc.text('Tel: (+225) 07 10 01 60 54 / 07 88 47 17 79 / 27 22 41 93 50', centerX, pageHeight - 14, { align: 'center' });
    doc.text('RCCM: CI-ABJ-03-2024-B13-023771 | NCC: 2400889R | infos@capassurances.com', centerX, pageHeight - 9, { align: 'center' });

    doc.save(`bordereau_${policeNum}_${dateReg.replace(/\//g, '-')}.pdf`);
}
